import _ from 'lodash';
import React, { ChangeEvent, useContext, useEffect, useRef, useState } from 'react';
import { TweenLite } from 'gsap';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import ReactQuill from 'react-quill';
import Dialog from 'src/components/common/Dialog.redesign';
import Button from 'src/components/controls/forms/redesign/FormButton';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';
import Acknowledgement from 'src/models/leasing/Acknowledgement';
import AcknowledgementType from 'src/models/leasing/AcknowledgementType';
import SignatureBlock from 'src/models/leasing/SignatureBlock';
import { AppContext } from 'src/providers/AppContextProvider';
import { IAppContextState } from 'src/reducers/AppContext';
import useFetch from 'use-http';
import SignatureCard from './SignatureBlockComponents/SignatureCard.redesign';
import RHFLegend from 'src/components/controls/tempRHFControls/RHFLegend';
import { sanitizeHtml } from 'src/components/common/UtilityFunctions';

interface IAcknowledgementDialogRTE {
    acknowledgement?: Acknowledgement;
    acknowledgementTypes?: AcknowledgementType[];
    newAcknowledgement?: boolean;
    refreshLease?: (refresh: boolean) => void;
    invoiceableLookupId?: number;
    onCloseDialog?: () => void;
}

const AcknowledgementDialogRTE = (props: IAcknowledgementDialogRTE) => {
    // const signTable = '<br><table>'
    //      + '<tr><td>________________________________________</td><td>________________________________________</td></tr><tr><td>[SIGNATURE NAME/TITLE]</td><td>[SIGNATURE NAME/TITLE]</td></tr>'
    //      + '</table><br>';

    const editor = useRef<ReactQuill>();
    const instructToggle = useRef();

    const modules = {
        toolbar: [
            [{ 'header': [1, 2, false] }],
            ['bold', 'italic', 'underline', 'strike', 'blockquote'],
            [{'list': 'ordered'}, {'list': 'bullet'}, {'indent': '-1'}, {'indent': '+1'}],
            [{'align': [] as string[]}],
            ['link', 'image'],
        ],
    };
    
    const formats = [
        'header',
        'bold', 'italic', 'underline', 'strike', 'blockquote',
        'list', 'bullet', 'indent',
        'align',
        'link', 'image'
    ];

    const sigStyles = [
        { value: 'Stacked', label: 'Stacked' },
        { value: 'Side By Side', label: 'Side By Side' }
    ];

    const { register, errors, handleSubmit, reset, control, setValue, getValues, watch } = useForm<Acknowledgement>({
        shouldUnregister: false,
        defaultValues: {
            id: 0,
            typeId: 0,
            textRTE: '',
            signatureStyle: 'Stacked',
            signatureBlocks: [] as SignatureBlock[]
        }
    });
    const { fields, append, remove } = useFieldArray<SignatureBlock>({ control, name: 'signatureBlocks' });
    const { post, put, del, response } = useFetch();
    const appContext = useContext<IAppContextState>(AppContext);

    const [ackPreview, setAckPreview] = useState<string>('');
    const [signatureOnly, setSignatureOnly] = useState<boolean>(false);

    // Watch values
    const ackRTE = watch('textRTE');
    const signatureStyle = watch('signatureStyle');
    const signatureBlocks = watch('signatureBlocks');

    useEffect(() => {
        if (!!props?.acknowledgement && !props?.newAcknowledgement) {
            prepAckData();
        }
    }, [props]);

    useEffect(() => {
        // NOTE: Using dangerouslySetHTML.
        // Do not set the watch value until html has been sanitized.
        let ackText = ackRTE;
        let cleanAck = sanitizeHtml(ackText);

        let signFormatted = '';
        if (signatureBlocks?.length > 0) {
            signFormatted = '<table style="width: 100%">';

            // TODO: update isDouble when Sign types are implemented.
            switch (signatureStyle) {
                case 'Two-Line':
                    let endRow = false;
                    for (let sign of signatureBlocks) {
                        let cleanSign = sanitizeHtml(sign.textRTE);
                        if (endRow) {
                            signFormatted += '<td>' + cleanSign + '</td></tr>';
                        } else {
                            signFormatted += '<tr><td>' + cleanSign + '</td>';
                        }
                        endRow = !endRow;    
                    }
                    if (endRow) {
                        signFormatted += '</tr>';
                    }
                    break;
                case 'Single':
                default:
                    for (let sign of signatureBlocks) {
                        let cleanSign = sanitizeHtml(sign.textRTE);
                        signFormatted += '<tr><td>' + cleanSign + '</td></tr>';
                    }
                    break;
            }
            
            signFormatted += '</table>';
        }

        cleanAck = cleanAck.replace('{signatureBlocks}', '</p>' + signFormatted + '<p>');

        setAckPreview(cleanAck);
    }, [watch]);
    
    useEffect(() => {
        if (!!props?.acknowledgement && !props?.newAcknowledgement) {
            prepAckData(true);
        }
    }, [signatureOnly]);

    const toggleInstruction = (state: boolean, e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
        if (state) {
            const rect = e.currentTarget.getBoundingClientRect();
            const containerRect = e.currentTarget.parentElement.getBoundingClientRect();

            TweenLite.set(
                instructToggle.current,
                { top: `${rect.bottom - containerRect.top - 20}`, left: `${rect.left - containerRect.left + 30}`, height: 'auto', display: 'inline-block', background: 'white', border: '2px solid #6393C1', zIndex: 100 }
            );
        } else {
            TweenLite.to(instructToggle.current, 0.2, { height: '2.5rem', display: 'none', background: 'transparent', borderColor: 'transparent', zIndex: 0 });
        }
    };

    const prepAckData = (preserveType: boolean = false) => {
        const ack = props.acknowledgement;
        if (preserveType) {
            ack.typeId = parseInt(getValues()?.typeId + '', 10);
            ack.textRTE = getValues()?.textRTE;
        }

        setSignatureOnly(props.acknowledgementTypes.find(at => at.name === 'Signature Only' && at.id === ack.typeId) !== undefined);
        
        reset({
            id: ack.id,
            stateId: ack.stateId,
            contractLookupId: ack.contractLookupId,
            typeId: ack.typeId,
            text: ack.text,
            textRTE: ack.textRTE,
            signatureStyle: ack.signatureStyle,
            signatureBlocks: ack.signatureBlocks
        });
    };

    const finalizeData = (data: Acknowledgement) => {
        data.id = data.id ? parseInt(data.id + '', 10) : 0;
        data.invoiceableLookupId = props.invoiceableLookupId;

        data.stateId = !!data.stateId ? parseInt(data.stateId + '', 10) : null;
        data.typeId = !!data.typeId ? parseInt(data.typeId + '', 10) : null;
        data.contractLookupId = !!data.contractLookupId ? parseInt(data.contractLookupId + '', 10) : null;
        data.text = !!data.text ? data.text : '';
        data.textRTE = !!data.textRTE ? data.textRTE : '';

        if (!props?.newAcknowledgement) {
            data.id = !!props.acknowledgement ? props?.acknowledgement?.id : 0;
            data.contractLookupId = !!props.acknowledgement ? props?.acknowledgement?.contractLookupId : 0;
        }

        if (data.signatureBlocks?.length > 0) {
            let index = 0;

            for (let sign of data.signatureBlocks) {
                // NOTE: For some reason, the id and acknowledgementId fields are completely ignored by the form.
                // hidden inputs, append, setValue, and reset all failed.
                sign.id = index < props.acknowledgement?.signatureBlocks?.length ? props.acknowledgement.signatureBlocks[index].id : 0;
                sign.acknowledgementId = props?.acknowledgement?.id ?? 0;

                sign.textRTE = !!sign.textRTE ? sign.textRTE : '';
                sign.title = !!sign.title ? sign.title : '';
                sign.ownerName = !!sign.ownerName ? sign.ownerName : '';

                index++;
            }
        }

        return data;
    };

    const onSubmit = (data: Acknowledgement) => {
        let newData = finalizeData(data);

        if (props?.newAcknowledgement && newData.id === 0) {
            createNewAcknowledgment(newData);
        }

        if (!props?.newAcknowledgement) {
            updateAcknowledgement(newData);
        }
    };

    const createNewAcknowledgment = async (data: Acknowledgement) => {
        if (!!props.invoiceableLookupId) {
            await post(
                `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/contractGeneration/acknowledgements`,
                data
            );
    
            if (response.ok) {
                props.refreshLease(true);
                setTimeout(() => {
                    props.onCloseDialog();
                }, 500);
            } else {
                window.alert('Create Acknowledgment failed.');
            }
        } else {         
            console.log('Invalid Invoiceable Lookup Id:', props.invoiceableLookupId);
            window.alert('Create Acknowledgment failed.');
        }
    };

    const updateAcknowledgement = async (data: Acknowledgement) => {
        await put(`/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/contractGeneration/acknowledgements`, data);

        if (response.ok) {
            props.refreshLease(true);
            props.onCloseDialog();
        } else {
            window.alert('Update Acknowledgment failed.');
        }
    };

    const handleAckTypeChange = (e: ChangeEvent<HTMLSelectElement>) => {
        let value: number = parseInt(e.target.value + '', 10);
        let ackType = props.acknowledgementTypes.find(at => at.id === value);

        if (value === props?.acknowledgement?.typeId) {
            setValue('textRTE', props.acknowledgement.textRTE);
        } else {
            let hasSign = getValues()?.signatureBlocks?.length > 0;
    
            let addSign = hasSign && ackType.name !== 'Signature Only' ? '<p>{signatureBlocks}</p><br>' : '';
    
            if (ackType?.description?.length > 0) {
                setValue('textRTE', addSign + ackType.description);
            }
        }

        if (ackType.name === 'Signature Only') {
            setSignatureOnly(true);
        } else {
            setSignatureOnly(false);
        }
    };

    const addSignature = () => {
        let currForm = getValues();
        if (!currForm.signatureBlocks || currForm.signatureBlocks.length === 0) {
            let addSign = '<p>{signatureBlocks}</p><br>';

            setValue('textRTE', addSign + currForm.textRTE);
        }

        append({
            id: 0,
            acknowledgementId: props?.acknowledgement?.id ?? 0,
            textRTE: '<p>________________________________________</p><p>[SIGNATURE NAME/TITLE]</p>'
        });
    };

    const deleteSignature = async (index: number) => {
        console.log('Current Values: ', getValues());

        let signatureFromAck: SignatureBlock | undefined = props.acknowledgement?.signatureBlocks?.[index];
        if (!!signatureFromAck) {
            const result = await del(
                `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/contractGeneration/signature/${signatureFromAck.id}`
            );

            if (response.ok) {
                console.log('Successfully Delete Signagture Block', result);
                props?.refreshLease(true);
            } else {
                console.log('Failed to Delete Signature from Acknowledgment!');
            }
        }

        remove(index);
    };

    // const displaySignatures = () => {
    //     let signatures = getValues()?.signatureBlocks;

    //     if (signatures?.length > 0) {
    //         return signatures.map((signature: SignatureBlock, index: number) => {
    //             return (
    //                 <SignatureCard
    //                     key={index}
    //                     signatureInfo={signature}
    //                     index={index}
    //                     register={register}
    //                     control={control}
    //                     ackType={!!props?.acknowledgementTypes ? props?.acknowledgementTypes : []}
    //                     deleteCard={deleteSignature}
    //                 />
    //             );
    //         });
    //     } else {
    //         return <div>No Signature for this Acknowledgement</div>;
    //     }
    // };

    return (
        <Dialog title={!!props?.newAcknowledgement ? 'New Acknowledgment' : 'Update Acknowledgment'}>
            <div style={{ width: '45vw', maxHeight: '80vh', overflowY: 'scroll'}}>
                <form onSubmit={handleSubmit(onSubmit)} id="acknowledgementForm">
                    <div style={{ marginTop: '1rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'center', position: 'relative'}}>
                            <RHFLegend title="Signature Blocks" />
                            <span
                                style={{fontSize: '20px'}}
                                onMouseOver={(e) => toggleInstruction(true, e)}
                                onMouseOut={() => toggleInstruction(false, null)}
                            >
                                👁‍🗨
                            </span>
                            <span
                                ref={instructToggle}
                                style={{
                                    borderRadius: '5px',
                                    border: '2px solid #6393C1',
                                    display: 'none',
                                    textOverflow: 'ellipsis',
                                    overflow: 'hidden',
                                    position: 'absolute',
                                    padding: '.3rem',
                                    background: 'transparent',
                                    borderColor: 'transparent',
                                    zIndex: 10,
                                    height: 'auto',
                                }}
                            >
                                Format your signatures as needed and then place the signatureBlocks token in the desired location. 
                                Select "Signature Only" from the acknowledgement types for direct signature names/titles.
                            </span>
                        </div>
                        {fields.map((field, index) => (
                            <div style={{ maxHeight: '20vh', overflow: 'auto' }} key={field.id}>
                                <SignatureCard
                                    index={index}
                                    register={register}
                                    control={control}
                                    signature={field}
                                    ackType={!!props?.acknowledgementTypes ? props?.acknowledgementTypes : []}
                                    signatureOnly={signatureOnly}
                                    deleteCard={deleteSignature}
                                />
                            </div>
                        ))}
                    </div>                    
                    <div style={{ marginTop: '2rem', display: 'flex', gap: '2rem' }}>
                        <Button 
                            text="Add Signature" 
                            onClick={addSignature} 
                        />
                    </div>
                    <div style={{ gap: '1rem', justifyContent: 'center' }}>
                        <div style={{ display: 'flex', justifyContent: 'center'}}>
                            <RHFLegend title="Acknowledgment Body" />
                        </div>
                        <div style={{ display: 'flex' }}>
                            <RHFSelect
                                label="Acknowledgment Type"
                                name="typeId"
                                options={
                                    !!props.acknowledgementTypes
                                        ? props.acknowledgementTypes.map((ackT) => {
                                            return { value: ackT.id, label: `${ackT.name}` };
                                        })
                                        : []
                                }
                                onChange={handleAckTypeChange}
                                register={register}
                                errors={errors}
                            />
                            <RHFSelect
                                label="Signature Style"
                                name="signatureStyle"
                                options={sigStyles}
                                register={register}
                                errors={errors}
                            />
                        </div>
                    </div>
                    {!signatureOnly && (
                        <>
                            <div style={{ width: '98%' }}>
                                <Controller
                                    name="textRTE"
                                    control={control}
                                    defaultValue=""
                                    render={({ onChange, onBlur, value }) => (
                                        <ReactQuill 
                                            ref={editor} 
                                            theme="snow"
                                            modules={modules}
                                            formats={formats}
                                            value={value} 
                                            onChange={onChange} 
                                            onBlur={onBlur} 
                                        />
                                    )}
                                />
                            </div>
                            <div style={{ marginTop: '1rem' }}>
                                <div style={{ display: 'flex', justifyContent: 'center'}}>
                                    <RHFLegend title="Preview" />
                                </div>
                                <div dangerouslySetInnerHTML={{ __html: ackPreview }} />
                            </div>                    
                        </>
                    )}
                    <div style={{ marginTop: '3rem' }}>
                        <button
                            type="submit"
                            form="acknowledgementForm"
                            style={{ padding: '1rem', backgroundColor: '#6393c1', color: 'white', border: 'none' }}
                        >
                            Submit
                        </button>
                    </div>
                </form>
            </div>
        </Dialog>
    );
};

export default AcknowledgementDialogRTE;


// WEBPACK FOOTER //
// ./src/components/project/sections/LeasingComponents/redesign/AcknowledgementDialog.redesign.tsx